import React, {
  useCallback, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import Loader from '../../../components/loader/loader.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import FieldSelect from '../../../components/field-select/field-select.component';
import FieldMultiSelect from '../../../components/field-multi-select/field-multi-select.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import useScreenSmall from '../../../hooks/use-screen-small';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const ActivityListModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    isSaving,
    errorMessage,
    dealList,
    contactList,
    saveFormDeps,
    onChange,
    onSaveForm,
    onCloseModal,
    onContactSearch,
    onContactSearchClose,
    onActivityDealSearch,
    onActivityDealSearchClose,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [selectedDealId, setSelectedDealId]: [string, Function] = useState('');
  const [selectedInvestorContacts, setSelectedInvestorContacts]: [Contact[], Function] = useState([]);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);

  const menuItemDealList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: dealList,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
  }), [dealList]);

  const menuItemContactInvestorList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedInvestorContacts,
  }), [contactList, selectedInvestorContacts]);

  const resetForm: Function = useCallback((): void => {
    setSelectedDealId('');
    setSelectedInvestorContacts([]);
  }, []);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    resetForm();

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onSaveForm && selectedInvestorContacts && selectedInvestorContacts.length && selectedDealId) {
      const selectedInvestorContactIds: number[] = selectedInvestorContacts
        .map((contactItem: Contact): number => contactItem.id);

      onSaveForm(selectedDealId, selectedInvestorContactIds, (): void => {
        resetForm();
      });
    }
  }, [selectedInvestorContacts, selectedDealId, ...(saveFormDeps || [])]);

  const closeModalByClickAway: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(true);
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    resetForm();

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const investorContactsChange: Function = useCallback((contactValues: Contact): void => {
    setSelectedInvestorContacts(contactValues);

    if (onChange) {
      onChange();
    }
  }, []);

  const dealChange: Function = useCallback((dealIdValue: number): void => {
    setSelectedDealId(dealIdValue);

    if (onChange) {
      onChange();
    }
  }, []);

  return (
    <ModalLayout
      isOpen={isOpen}
      title="New Activities"
      onCloseModal={cancelChangesAndCloseModal}
      onCloseModalByClickAway={closeModalByClickAway}
    >
      <Card isContainer withNoPadding>
        <Table>
          <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
            <TableRow>
              <TableCell>
                Deal
                <RequiredMark />
              </TableCell>
              <TableCell>
                <FieldSelect
                  isPlain
                  isBordered
                  withSearch
                  withBlankElement
                  isDisabled={dealList && !dealList.length}
                  value={selectedDealId}
                  optionsTotal={dealList && dealList.length}
                  onSearch={onActivityDealSearch}
                  onClose={onActivityDealSearchClose}
                  onChange={(event: EventSelectChange, value: string): void => {
                    dealChange(value);
                  }}
                >
                  {menuItemDealList}
                </FieldSelect>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Investors
                <RequiredMark />
              </TableCell>
              <TableCell>
                <FieldMultiSelect
                  isPlain
                  isBordered
                  withImage
                  withSearch
                  nameKey="fullName"
                  isDisabled={contactList && !contactList.length}
                  onSearch={onContactSearch}
                  onClose={onContactSearchClose}
                  optionList={contactList}
                  onValuesChange={investorContactsChange}
                  values={selectedInvestorContacts}
                  defaultImage={CompanyDefaultImage}
                >
                  {menuItemContactInvestorList}
                </FieldMultiSelect>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
      <ModalButtonBlock>
        <Button onClick={cancelChangesAndCloseModal}>
          Cancel
        </Button>
        <Button isActive isDisabled={isSaving} onClick={applyChangesAndCloseModal}>
          Save
        </Button>
        {
          isSaving && !isSmallScreen && (
            <Loader isInline isModal isSmall />
          )
        }
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
      <LeaveConfirmModal
        isSubModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
    </ModalLayout>
  );
});

export default ActivityListModal;
